import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Card, CardBody, PageSection, Spinner } from "@patternfly/react-core";

import { apiGet } from "../../app/api";
import { compactDateTime, readableEventLabel } from "../../display";
import { StandardDataTable, type StandardColumn } from "../../components/StandardDataTable";

type AuditLogRow = {
  id?: number;
  user_id?: number;
  username?: string;
  full_name?: string;
  action?: string;
  entity_type?: string;
  entity_id?: string | number;
  created_at?: string;
};

type ChangeLogRow = {
  id?: number;
  table_name?: string;
  row_id?: string | number;
  action?: string;
  created_at?: string;
  username?: string;
  full_name?: string;
};

type LoginAttemptRow = {
  id?: number;
  attempted_username?: string;
  status?: string;
  reason?: string;
  ip_address?: string;
  user_agent?: string;
  created_at?: string;
};

export function AuditTrailWorkspace() {
  const [auditRows, setAuditRows] = useState<AuditLogRow[]>([]);
  const [changeRows, setChangeRows] = useState<ChangeLogRow[]>([]);
  const [loginRows, setLoginRows] = useState<LoginAttemptRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ variant: "success" | "danger"; title: string } | null>(null);

  const loadAudit = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [auditPayload, changePayload, loginPayload] = await Promise.all([
        apiGet<{ audit_log?: AuditLogRow[] }>("/api/audit-log"),
        apiGet<{ change_log?: ChangeLogRow[] }>("/api/change-log"),
        apiGet<{ login_attempts?: LoginAttemptRow[] }>("/api/login-attempts")
      ]);
      setAuditRows(auditPayload.audit_log || []);
      setChangeRows(changePayload.change_log || []);
      setLoginRows(loginPayload.login_attempts || []);
    } catch (err) {
      setMessage({ variant: "danger", title: err instanceof Error ? err.message : "Audit Trail could not be loaded." });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAudit();
  }, [loadAudit]);

  const auditColumns: StandardColumn<AuditLogRow>[] = useMemo(() => [
    { key: "created_at", label: "When", value: row => row.created_at || "", render: row => compactDateTime(row.created_at) },
    { key: "user", label: "User", value: row => row.full_name || row.username || "" },
    { key: "action", label: "Action", value: row => readableEventLabel(row.action) },
    { key: "entity_type", label: "Record Type", value: row => readableEventLabel(row.entity_type) },
    { key: "entity_id", label: "Record", value: row => row.entity_id || "" }
  ], []);

  const changeColumns: StandardColumn<ChangeLogRow>[] = useMemo(() => [
    { key: "created_at", label: "When", value: row => row.created_at || "", render: row => compactDateTime(row.created_at) },
    { key: "user", label: "User", value: row => row.full_name || row.username || "" },
    { key: "table_name", label: "Table", value: row => row.table_name || "" },
    { key: "row_id", label: "Record", value: row => row.row_id || "" },
    { key: "action", label: "Action", value: row => readableEventLabel(row.action) }
  ], []);

  const loginColumns: StandardColumn<LoginAttemptRow>[] = useMemo(() => [
    { key: "created_at", label: "When", value: row => row.created_at || "", render: row => compactDateTime(row.created_at) },
    { key: "attempted_username", label: "Username", value: row => row.attempted_username || "" },
    { key: "status", label: "Status", value: row => readableEventLabel(row.status) },
    { key: "reason", label: "Reason", value: row => row.reason || "" },
    { key: "ip_address", label: "IP Address", value: row => row.ip_address || "" },
    { key: "user_agent", label: "Browser", value: row => row.user_agent || "" }
  ], []);

  return (
    <PageSection className="pf-toj-workspace" hasBodyWrapper={false}>
      <div id="pf-audit-trail-view">
        <div className="pf-toj-workspace-title">
          <h1>Audit Trail</h1>
          <p>Review user activity, record changes, and login attempts.</p>
        </div>
        {message && <Alert variant={message.variant} title={message.title} isInline className="pf-toj-inline-alert" />}
        {loading ? <Spinner size="lg" aria-label="Loading Audit Trail" /> : (
          <div className="pf-toj-stacked-work">
            <Card className="pf-toj-work-card pf-toj-primary-work-card">
              <CardBody>
                <h2>User Activity</h2>
                <StandardDataTable ariaLabel="Audit log rows" rows={auditRows} columns={auditColumns} searchPlaceholder="Search user activity" />
              </CardBody>
            </Card>
            <Card className="pf-toj-work-card">
              <CardBody>
                <h2>Record Changes</h2>
                <StandardDataTable ariaLabel="Change log rows" rows={changeRows} columns={changeColumns} searchPlaceholder="Search record changes" />
              </CardBody>
            </Card>
            <Card className="pf-toj-work-card">
              <CardBody>
                <h2>Login Attempts</h2>
                <StandardDataTable ariaLabel="Login attempt rows" rows={loginRows} columns={loginColumns} searchPlaceholder="Search login attempts" />
              </CardBody>
            </Card>
          </div>
        )}
      </div>
    </PageSection>
  );
}
